import { BaseModel } from '@/api/base-model'
import { TypeformFormAPI } from '@/api/backend-client'
import { TypeformWorkspaceHref } from '@/api/model/typeform/typeform-workspace-href'
import { TypeformThemeHref } from '@/api/model/typeform/typeform-theme-href'
import { TypeformWelcomeScreen } from '@/api/model/typeform/typeform-welcome-screen'
import { TypeformThankYouScreen } from '@/api/model/typeform/typeform-thank-you-screen'
import { TypeformField, TypeformFieldUnion } from '@/api/model/typeform/typeform-field'
import { TypeformLogicJump } from '@/api/model/typeform/typeform-logic-jump'

export class TypeformForm extends BaseModel<TypeformForm> {
    protected collectionName = TypeformFormAPI.collectionName

    constructor(
        public id: string,
        public type: string,
        public title: string,
        public workspace: TypeformWorkspaceHref,
        public theme: TypeformThemeHref,
        public settings: TypeformSettings,
        public cui_settings: TypeformCuiSettings,
        public hidden: string[],
        public variables: TypeformVariables,
        public welcome_screens: TypeformWelcomeScreen[],
        public thankyou_screens: TypeformThankYouScreen[],
        public fields: TypeformField[],
        public logic: TypeformLogicJump[],
        public created_at: Date,
        public last_updated_at: Date,
    ) { super(id) }

    getOrCreateLogicForField(field: TypeformFieldUnion): TypeformLogicJump|null {
        let logicJump = this.getLogicForField(field)

        if (!logicJump) {
            logicJump = this.createLogicForField(field)
        }

        return logicJump
    }

    getLogicForField(field: TypeformFieldUnion): TypeformLogicJump|null {
        if (field instanceof TypeformField) {
            return this.logic.find(existingLogicJump => existingLogicJump.type === 'field' && existingLogicJump.ref === field.ref)
        }

        if (field instanceof TypeformWelcomeScreen) {
            return this.logic.find(existingLogicJump => existingLogicJump.type === 'hidden')
        }

        return null
    }

    createLogicForField(field: TypeformFieldUnion): TypeformLogicJump|null {
        if (field instanceof TypeformField) {
            return TypeformLogicJump.fromData({
                type: 'field',
                ref: field.ref,
            })
        }

        if (field instanceof TypeformWelcomeScreen) {
            return TypeformLogicJump.fromData({
                type: 'hidden',
            })
        }

        return null
    }

    get hasUnsupportedFeatures(): boolean {
        // Check unsupported fields are used
        for (const field of this.fields) {
            const unsupportedFields = ['group', 'matrix']
            if (unsupportedFields.includes(field.type)) {
                return true
            }
        }

        // Check variables are defined
        if (Object.keys(this.variables).length > 0) {
            return true
        }

        // Check logic jumps
        for (const logicJump of this.logic) {
            // Check actions
            for (const action of logicJump.actions) {
                const supportedActions = ['jump']
                if (!supportedActions.includes(action.action)) {
                    return true
                }

                // Check conditions
                for (const condition of action.flattenedConditions) {
                    for (const variable of condition.vars) {
                        const unsupportedTypes = ['variable']
                        if (unsupportedTypes.includes(variable.type)) {
                            return true
                        }
                    }
                }
            }
        }

        return false
    }

    toRequestData(): any {
        return {
            id: this.id,
            type: this.type,
            title: this.title,
            workspace: this.workspace,
            theme: this.theme,
            settings: this.settings,
            cui_settings: this.cui_settings,
            hidden: this.hidden,
            variables: this.variables,
            welcome_screens: this.welcome_screens.map(entry => entry.toRequestData()),
            thankyou_screens: this.thankyou_screens.map(entry => entry.toRequestData()),
            fields: this.fields.map(entry => entry.toRequestData()),
            logic: this.logic,
        }
    }

    static fromData(data: any): TypeformForm {
        if (data === null || typeof data === 'string' || data instanceof TypeformForm) {
            return data
        }

        return new TypeformForm(
            data.id,
            data.type,
            data.title,
            TypeformWorkspaceHref.fromData(data.workspace || null),
            TypeformThemeHref.fromData(data.theme || null),
            data.settings || {},
            data.cui_settings || {},
            data.hidden || [],
            data.variables || {},
            Object.values(data.welcome_screens || {}).map(entry => TypeformWelcomeScreen.fromData(entry)),
            Object.values(data.thankyou_screens || {}).map(entry => TypeformThankYouScreen.fromData(entry)),
            Object.values(data.fields || {}).map(entry => TypeformField.fromData(entry)),
            Object.values(data.logic || {}).map(entry => TypeformLogicJump.fromData(entry)),
            data.created_at,
            data.last_updated_at,
        )
    }
}

export interface TypeformCuiSettings {
    [key: string]: string|number
}

export interface TypeformSettings {
    [key: string]: string|number
}

export interface TypeformVariables {
    [key: string]: string|number
}

export const TypeformReferenceRegExp = new RegExp('(\{\{\\s*(hidden|field):(.*?)\\s*\}\})', 'g')
